/**
 * VMCreateDialog — Create a new VM on a registered host
 */

import React, { useState, useEffect } from 'react';
import { useInfraStore } from '@/stores/infrastructure/infra-store';
import type { VMCreateSpec } from '@/services/api/infrastructureApi';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

interface Props {
  open: boolean;
  hostId: string;
  onClose: () => void;
}

export const VMCreateDialog: React.FC<Props> = ({ open, hostId, onClose }) => {
  const { createVM, fetchTemplates, fetchNetworks, templates, networks, isLoading } = useInfraStore();
  const [form, setForm] = useState<VMCreateSpec>({
    name: '',
    templateName: '',
    cpuCount: 1,
    memoryMB: 1024,
    diskSizeGB: 20,
    autoStart: true,
    createServerConnection: false,
    description: '',
  });
  const [cloudInitRaw, setCloudInitRaw] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && hostId) {
      fetchTemplates(hostId);
      fetchNetworks(hostId);
    }
  }, [open, hostId, fetchTemplates, fetchNetworks]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!form.name || !form.templateName) {
      setError('VM name and template are required.');
      return;
    }

    const spec: VMCreateSpec = { ...form };

    // Parse cloud-init JSON if provided
    if (cloudInitRaw.trim()) {
      try {
        const cloudInit: unknown = JSON.parse(cloudInitRaw);
        if (cloudInit == null || typeof cloudInit !== 'object' || Array.isArray(cloudInit)) {
          setError('Cloud-init config must be a JSON object.');
          return;
        }
        spec.cloudInit = cloudInit;
      } catch {
        setError('Cloud-init config must be valid JSON.');
        return;
      }
    }

    const vm = await createVM(hostId, spec);
    if (vm) {
      setForm({ name: '', templateName: '', cpuCount: 1, memoryMB: 1024, diskSizeGB: 20, autoStart: true, createServerConnection: false, description: '' });
      setCloudInitRaw('');
      onClose();
    }
  };

  const update = (field: string, value: any) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[550px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Virtual Machine</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="vmName">VM Name *</Label>
              <Input
                id="vmName"
                placeholder="web-server-01"
                value={form.name}
                onChange={(e) => update('name', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="template">Template *</Label>
              <Select value={form.templateName} onValueChange={(v) => update('templateName', v)}>
                <SelectTrigger><SelectValue placeholder="Select template" /></SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.name}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="cpuCount">CPUs</Label>
              <Input
                id="cpuCount"
                type="number"
                min={1}
                max={128}
                value={form.cpuCount}
                onChange={(e) => update('cpuCount', parseInt(e.target.value) || 1)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="memoryMB">Memory (MB)</Label>
              <Input
                id="memoryMB"
                type="number"
                min={256}
                step={256}
                value={form.memoryMB}
                onChange={(e) => update('memoryMB', parseInt(e.target.value) || 1024)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="diskSizeGB">Disk (GB)</Label>
              <Input
                id="diskSizeGB"
                type="number"
                min={1}
                value={form.diskSizeGB || ''}
                onChange={(e) => update('diskSizeGB', parseInt(e.target.value) || undefined)}
              />
            </div>
          </div>

          {networks.length > 0 && (
            <div className="space-y-2">
              <Label>Network</Label>
              <Select onValueChange={(v) => update('networkIds', [v])}>
                <SelectTrigger><SelectValue placeholder="Select network" /></SelectTrigger>
                <SelectContent>
                  {networks.map((n) => (
                    <SelectItem key={n.id} value={n.id}>
                      {n.name} {n.bridge ? `(${n.bridge})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="cloudInit">Cloud-Init Config (JSON)</Label>
            <Textarea
              id="cloudInit"
              placeholder={'{\n  "hostname": "web-01",\n  "packages": ["docker.io", "nginx"],\n  "sshAuthorizedKeys": ["ssh-ed25519 ..."]\n}'}
              value={cloudInitRaw}
              onChange={(e) => setCloudInitRaw(e.target.value)}
              rows={4}
              className="font-mono text-sm"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              placeholder="Web server for staging environment"
              value={form.description || ''}
              onChange={(e) => update('description', e.target.value)}
            />
          </div>

          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Checkbox
                id="autoStart"
                checked={form.autoStart}
                onCheckedChange={(v) => update('autoStart', v)}
              />
              <Label htmlFor="autoStart" className="text-sm">Auto-start after creation</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="createServerConnection"
                checked={form.createServerConnection}
                onCheckedChange={(v) => update('createServerConnection', v)}
              />
              <Label htmlFor="createServerConnection" className="text-sm">Register for SSH</Label>
            </div>
          </div>

          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Creating...' : 'Create VM'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
